import { Component, OnDestroy, OnInit } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { HasAnalytics, HasPageTitleAndMetaData, VideoInterface } from '@app/interfaces/_index';
import { GoogleAnalyticsService, WordpressVideosService } from '@app/services/_index';

import { VideoAdapter } from '@app/adapters/_index';
import { Video } from '@app/models/_index';
import { logGoogleAnalytics } from '@app/utilities/_index';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-videos',
  templateUrl: './videos.component.html',
  styleUrls: ['./videos.component.scss'],
  standalone: false,
})
export class VideosComponent implements OnInit, OnDestroy, HasPageTitleAndMetaData, HasAnalytics {
  isLoading: boolean;
  featuredVideos: Video[] = [];
  subs: Subscription[] = [];
  sub: Subscription;

  constructor(
    private titleService: Title,
    private metaService: Meta,
    private googleAnalyticsService: GoogleAnalyticsService,
    private videoAdapter: VideoAdapter,
    private wpVideoService: WordpressVideosService
  ) {}

  /**
   * OnInit setting page title and metadata
   * Set featured videos, recommended videos and video categories needed for the page.
   */
  ngOnInit(): void {
    // Set title and metadata
    this.setTitleAndMetaData();
    this.getWpFeaturedVideos(); // To load the data from Wordpress source
  }

  /**
   * On destroy unsubscribe the subscription to videos, to prevent
   * memory leaks
   */
  ngOnDestroy(): void {
    if (this.subs) {
      this.subs.forEach((sub) => sub.unsubscribe());
    }
  }

  /**
   * Set page title and metadata tags
   */
  setTitleAndMetaData(): void {
    // Set title for default main page
    this.titleService.setTitle('Videos | Genesys Education');
    // Set meta tag for description for beyond
    this.metaService.updateTag({
      name: 'description',
      content:
        'Learn new technologies and the necessary business skills to grow in the world of customer experience, including artificial intelligence, workforce management and more.',
    });
  }

  /**
   * Send the query search label to Google Analytics
   * @param category - the source of the google analytics category string (ex. the page or component triggering the action)
   * @param action - the action to be (ex. click)
   * @param dataLabel - the label associated with the action
   */
  sendGoogleAnalytics(category: string, action: string, dataLabel: string): void {
    logGoogleAnalytics(category, action, dataLabel);
    this.googleAnalyticsService.sendEvent(category, action, dataLabel);
  }

  /**
   * Load Featured Videos from Wordpress
   */
  getWpFeaturedVideos(): void {
    this.isLoading = true;
    this.subs.push(
      this.wpVideoService.getWpFeaturedVideos().subscribe({
        next: (videos: VideoInterface[]) => {
          videos.forEach((video: VideoInterface) => {
            this.featuredVideos.push(this.videoAdapter.adapt(video));
          });
          this.isLoading = false;
        },
        error: (error) => {
          this.isLoading = false;
          console.log(error);
        },
        complete: () => {
          // Force is loading to stop and complete in case of any unhandled errors
          this.isLoading = false;
          console.log('Featured Videos content completed');
        },
      })
    );
  }

  /**
   * Add Track By Featured Video Id to not redraw or re-render list items that have not changed.
   * This improves performance.
   * @param index - Index of Featured Video array
   * @param item - Featured Video item
   * @returns Featured Video id
   */
  trackByFeaturedVideoId(index: number, item: Video): string {
    if (item) {
      return item.id;
    } else {
      return String(index);
    }
  }
}
